import { useCallback, useEffect, useState } from "react";
import { PackageCheck, ClipboardEdit, CheckCircle2, Save, Paperclip, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { ProjectScopeBanner } from "../components/common/ProjectScopeBanner";
import { DataTable } from "../components/common/DataTable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_COLORS = { PENDING_STORE_RECEIPT: "bg-amber-500", RECEIVED_AT_STORE: "bg-blue-600", RETURN_DETAILS_COMPLETED: "bg-emerald-600" };

export default function OldMeterReturns() {
  const { has } = useAuth();
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id;
  const canReceive = has("OLD_METER_RETURN.RECEIVE");
  const [refreshKey, setRefreshKey] = useState(0);
  const [stores, setStores] = useState([]);
  const [receiveFor, setReceiveFor] = useState(null);
  const [receiveForm, setReceiveForm] = useState({ store_id: "", actual_receipt_date: new Date().toISOString().slice(0, 10), remarks: "" });
  const [detailsFor, setDetailsFor] = useState(null);
  const [rf, setRf] = useState(null);           // return form render {custom_fields, attachments, values, message}
  const [rfValues, setRfValues] = useState({});
  const [rfAtt, setRfAtt] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/stores", { params: { page_size: 500 } }).then((r) => setStores(r.data.items || [])).catch(() => {}); }, []);
  useEffect(() => { setRefreshKey((k) => k + 1); }, [pid]);

  const loadPending = (params) => api.get("/old-meter-returns/pending", { params: { ...params, ...(pid ? { project_id: pid } : {}) } }).then((r) => r.data);
  const loadByStatus = (status) => (params) => api.get("/old-meter-returns", { params: { ...params, status, ...(pid ? { project_id: pid } : {}) } }).then((r) => r.data);

  const openReceive = (row) => {
    setReceiveFor(row);
    setReceiveForm({ store_id: "", actual_receipt_date: new Date().toISOString().slice(0, 10), remarks: "" });
  };
  const doReceive = async () => {
    if (!receiveForm.store_id) { toast.error("Select a store"); return; }
    setBusy(true);
    try {
      await api.post(`/old-meter-returns/${receiveFor.id}/receive`, { ...receiveForm, idempotency_key: crypto.randomUUID() });
      toast.success("Old meter received at store"); setReceiveFor(null); setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Receive failed"); }
    finally { setBusy(false); }
  };

  const projectStores = (pid) => stores.filter((s) => s.project_id === pid && s.is_active !== false);

  const openDetails = async (row) => {
    setDetailsFor(row);
    const { data } = await api.get(`/old-meter-returns/${row.id}/return-form`);
    setRf(data); setRfValues(data.values || {});
    const { data: at } = await api.get(`/old-meter-returns/${row.id}/attachments`);
    setRfAtt(at.items || []);
  };
  const uploadFiles = async (fileList) => {
    const fd = new FormData();
    Array.from(fileList).forEach((f) => fd.append("files", f));
    try {
      await api.post(`/old-meter-returns/${detailsFor.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/old-meter-returns/${detailsFor.id}/attachments`); setRfAtt(data.items || []); toast.success("Attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };
  const delAtt = async (aid) => { try { await api.delete(`/old-meter-returns/attachments/${aid}`); setRfAtt((a) => a.filter((x) => x.id !== aid)); } catch (e) { toast.error("Failed"); } };
  const saveDetailsDraft = async () => {
    setBusy(true);
    try { await api.post(`/old-meter-returns/${detailsFor.id}/return-details/draft`, { values: rfValues, attachment_ids: rfAtt.map((a) => a.id) }); toast.success("Draft saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  const completeDetails = async () => {
    setBusy(true);
    try {
      await api.post(`/old-meter-returns/${detailsFor.id}/return-details/complete`, { values: rfValues, attachment_ids: rfAtt.map((a) => a.id), idempotency_key: crypto.randomUUID() });
      toast.success("Return details completed"); setDetailsFor(null); setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Complete failed"); } finally { setBusy(false); }
  };

  const baseCols = [
    { key: "old_meter_no", label: "Old Meter", mono: true },
    { key: "consumer_number", label: "Consumer", mono: true, render: (r) => r.consumer_number || "—" },
    { key: "consumer_name", label: "Name", hideMobile: true, render: (r) => r.consumer_name || "—" },
    { key: "new_meter_serial", label: "New Meter", mono: true, render: (r) => r.new_meter_serial || "—" },
    { key: "installation_date", label: "Replaced", hideMobile: true, render: (r) => r.installation_date || "—" },
    { key: "data_entry_number", label: "Data Entry", mono: true, hideMobile: true },
  ];
  const statusCol = { key: "status", label: "Status", render: (r) => <Badge className={STATUS_COLORS[r.status] || "bg-slate-500"}>{(r.status || "").replace(/_/g, " ")}</Badge> };

  return (
    <div>
      <PageHeader title="Old Meter Returns" subtitle="Receive returned old meters & complete return documentation" />
      <ProjectScopeBanner />
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="omr-tab-pending">Pending Receipt</TabsTrigger>
          <TabsTrigger value="received" data-testid="omr-tab-received">Received / Details Pending</TabsTrigger>
          <TabsTrigger value="completed" data-testid="omr-tab-completed">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <DataTable testid="omr-pending" columns={[...baseCols, statusCol]} load={loadPending} refreshKey={refreshKey} searchPlaceholder="Search old meter / consumer / entry..."
            rowActions={canReceive ? (row) => <Button data-testid={`omr-receive-${row.id}`} size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => openReceive(row)}><PackageCheck className="mr-1 h-4 w-4" /> Receive</Button> : null} />
        </TabsContent>
        <TabsContent value="received" className="mt-4">
          <DataTable testid="omr-received" columns={[...baseCols, { key: "receiving_store_name", label: "Store", render: (r) => r.receiving_store_name || "—" }, statusCol]} load={loadByStatus("RECEIVED_AT_STORE")} refreshKey={refreshKey} searchPlaceholder="Search..."
            rowActions={(row) => <Button data-testid={`omr-details-${row.id}`} size="sm" variant="outline" onClick={() => openDetails(row)}><ClipboardEdit className="mr-1 h-4 w-4" /> Return Details</Button>} />
        </TabsContent>
        <TabsContent value="completed" className="mt-4">
          <DataTable testid="omr-completed" columns={[...baseCols, { key: "receiving_store_name", label: "Store", render: (r) => r.receiving_store_name || "—" }, statusCol]} load={loadByStatus("RETURN_DETAILS_COMPLETED")} refreshKey={refreshKey} searchPlaceholder="Search..." />
        </TabsContent>
      </Tabs>

      {/* Receive dialog */}
      <Dialog open={!!receiveFor} onOpenChange={(o) => !o && setReceiveFor(null)}><DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Receive Old Meter · {receiveFor?.old_meter_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Receiving Store *</Label>
            <Select value={receiveForm.store_id} onValueChange={(v) => setReceiveForm({ ...receiveForm, store_id: v })}>
              <SelectTrigger className="mt-1" data-testid="omr-store-select"><SelectValue placeholder="Select project store" /></SelectTrigger>
              <SelectContent>{projectStores(receiveFor?.project_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.store_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Actual Receipt Date *</Label><Input type="date" data-testid="omr-receipt-date" className="mt-1" value={receiveForm.actual_receipt_date} onChange={(e) => setReceiveForm({ ...receiveForm, actual_receipt_date: e.target.value })} /></div>
          <div><Label className="text-xs">Remarks</Label><Input data-testid="omr-receipt-remarks" className="mt-1" value={receiveForm.remarks} onChange={(e) => setReceiveForm({ ...receiveForm, remarks: e.target.value })} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setReceiveFor(null)}>Cancel</Button><Button data-testid="omr-receive-confirm" onClick={doReceive} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Confirm Receipt</Button></DialogFooter>
      </DialogContent></Dialog>

      {/* Return details dialog */}
      <Dialog open={!!detailsFor} onOpenChange={(o) => !o && setDetailsFor(null)}><DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Return Details · {detailsFor?.old_meter_no}</DialogTitle></DialogHeader>
        {rf && !rf.version_id && <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700" data-testid="omr-no-form">{rf.message || "No published Old Meter Return Form configured for this project."}</div>}
        {rf && rf.version_id && (
          <div className="space-y-4" data-testid="omr-return-form">
            {rf.version_no && <Badge variant="outline">Return Form v{rf.version_no}</Badge>}
            <div className="grid gap-4 sm:grid-cols-2">
              {(rf.custom_fields || []).filter((c) => c.type !== "attachment").map((cf) => (
                <RFField key={cf.field_id} cf={cf} value={rfValues[cf.field_id]} onChange={(v) => setRfValues((s) => ({ ...s, [cf.field_id]: v }))} />
              ))}
            </div>
            {rf.attachments?.enabled && (
              <div>
                <input type="file" multiple id="omr-file" data-testid="omr-file-input" className="hidden" onChange={(e) => uploadFiles(e.target.files)} />
                <Button variant="outline" size="sm" onClick={() => document.getElementById("omr-file").click()}><Paperclip className="mr-1 h-4 w-4" /> Upload (max {rf.attachments.max_count})</Button>
                <div className="mt-2 space-y-1">
                  {rfAtt.map((a) => <div key={a.id} className="flex items-center justify-between rounded border px-3 py-1.5 text-sm"><span>{a.original_filename}</span><Button variant="ghost" size="sm" onClick={() => delAtt(a.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>)}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={saveDetailsDraft} disabled={busy} data-testid="omr-save-draft"><Save className="mr-1 h-4 w-4" /> Save Draft</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={completeDetails} disabled={busy} data-testid="omr-complete"><CheckCircle2 className="mr-1 h-4 w-4" /> Complete Return Details</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent></Dialog>
    </div>
  );
}

function RFField({ cf, value, onChange }) {
  const label = <Label className="text-[11px]">{cf.label} {cf.required && <span className="text-red-500">*</span>}</Label>;
  const tid = `omr-cf-${cf.field_id}`;
  if (cf.type === "dropdown") return <div>{label}<Select value={value || ""} onValueChange={onChange}><SelectTrigger className="mt-1 h-9" data-testid={tid}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{(cf.options || []).map((o) => <SelectItem key={o.option_id} value={o.option_id}>{o.label}</SelectItem>)}</SelectContent></Select></div>;
  if (cf.type === "multiselect") return <div>{label}<div className="mt-1 flex flex-wrap gap-2">{(cf.options || []).map((o) => { const arr = Array.isArray(value) ? value : []; const on = arr.includes(o.option_id); return <button key={o.option_id} data-testid={`${tid}-${o.option_id}`} onClick={() => onChange(on ? arr.filter((x) => x !== o.option_id) : [...arr, o.option_id])} className={`rounded-full border px-3 py-1 text-xs ${on ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600"}`}>{o.label}</button>; })}</div></div>;
  if (cf.type === "boolean") return <div className="flex items-center gap-2 pt-5"><Checkbox checked={!!value} onCheckedChange={(v) => onChange(!!v)} data-testid={tid} />{label}</div>;
  const t = cf.type === "number" ? "number" : cf.type === "date" ? "date" : cf.type === "datetime" ? "datetime-local" : "text";
  return <div>{label}<Input type={t} data-testid={tid} className="mt-1 h-9" value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>;
}
